import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ColorPicker.css';

/**
 * Componente de seleção de cores personalizado com interface modal
 * @param {Object} props - Propriedades do componente
 * @param {string} props.value - Valor atual da cor (formato hex)
 * @param {Function} props.onChange - Função chamada quando a cor muda
 * @param {string} props.name - Nome do campo para ser passado no evento onChange
 * @param {string} props.id - ID do elemento
 */
const ColorPicker = ({ value, onChange, name, id }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentColor, setCurrentColor] = useState(value || '#4682B4');
  const modalRef = useRef(null);
  
  // Cores predefinidas populares para investimentos
  const presetColors = [
    '#4682B4', // Steel Blue
    '#6A5ACD', // Slate Blue
    '#FF7F50', // Coral
    '#3CB371', // Medium Sea Green
    '#20B2AA', // Light Sea Green
    '#9370DB', // Medium Purple
    '#48D1CC', // Turquoise
    '#FF69B4', // Hot Pink
    '#DDA0DD', // Plum
    '#FFD700', // Gold
    '#B8860B', // Dark Goldenrod
    '#CD5C5C', // Indian Red
  ];
  
  useEffect(() => {
    setCurrentColor(value);
  }, [value]);
  
  useEffect(() => {
    // Impede rolagem do body quando o modal está aberto
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Adiciona listener para fechar ao pressionar ESC
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isModalOpen]);
  
  const openModal = () => {
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setCurrentColor(newColor);
    
    // Criar um evento sintético para emular o comportamento do input padrão
    if (onChange) {
      const syntheticEvent = {
        target: {
          name,
          value: newColor
        }
      };
      onChange(syntheticEvent);
    }
  };
  
  const handlePresetClick = (color) => {
    setCurrentColor(color);
    
    // Criar um evento sintético
    if (onChange) {
      const syntheticEvent = {
        target: {
          name,
          value: color
        }
      };
      onChange(syntheticEvent);
    }
    
    closeModal();
  };
  
  // Função para determinar o valor de contraste de texto para determinada cor
  const getContrastColor = (hexColor) => {
    // Converter hex para RGB
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    
    // Calcular luminosidade (fórmula YIQ)
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    
    // Retorna preto ou branco dependendo da luminosidade
    return (yiq >= 128) ? '#000000' : '#FFFFFF';
  };
  
  // Renderiza o modal usando createPortal para garantir que esteja no final do body
  const renderModal = () => {
    if (!isModalOpen) return null;
    
    return createPortal(
      <div className="color-modal-backdrop" onClick={closeModal}>
        <div 
          className="color-modal" 
          onClick={(e) => e.stopPropagation()}
          ref={modalRef}
        >
          <div className="color-modal-header">
            <h3>Selecionar cor</h3>
            <button 
              type="button" 
              className="color-modal-close" 
              onClick={closeModal}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
          
          <div className="color-modal-content">
            <div className="color-input-wrapper">
              <label htmlFor={id || 'color-input'}>Cor atual</label>
              <div className="color-preview-large" style={{ backgroundColor: currentColor }}>
                <input
                  type="color"
                  id={id || 'color-input'}
                  value={currentColor}
                  onChange={handleColorChange}
                  className="color-input-large"
                />
              </div>
              <div className="color-hex-value">{currentColor}</div>
            </div>
            
            <div className="color-presets-section">
              <h4>Cores sugeridas</h4>
              <div className="color-presets-grid">
                {presetColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className="color-preset-button"
                    style={{ backgroundColor: color }}
                    onClick={() => handlePresetClick(color)}
                    aria-label={`Selecionar cor ${color}`}
                    title={color}
                  />
                ))}
              </div>
            </div>
            
            <div className="color-rgb-controls">
              <div className="color-value-item">
                <label>R</label>
                <input 
                  type="number" 
                  value={parseInt(currentColor.substr(1, 2), 16)} 
                  min="0" 
                  max="255"
                  onChange={(e) => {
                    const r = Math.min(255, Math.max(0, parseInt(e.target.value) || 0)).toString(16).padStart(2, '0');
                    const newColor = `#${r}${currentColor.substr(3, 4)}`;
                    handleColorChange({ target: { value: newColor } });
                  }}
                />
              </div>
              <div className="color-value-item">
                <label>G</label>
                <input 
                  type="number" 
                  value={parseInt(currentColor.substr(3, 2), 16)} 
                  min="0" 
                  max="255"
                  onChange={(e) => {
                    const g = Math.min(255, Math.max(0, parseInt(e.target.value) || 0)).toString(16).padStart(2, '0');
                    const newColor = `#${currentColor.substr(1, 2)}${g}${currentColor.substr(5, 2)}`;
                    handleColorChange({ target: { value: newColor } });
                  }}
                />
              </div>
              <div className="color-value-item">
                <label>B</label>
                <input 
                  type="number" 
                  value={parseInt(currentColor.substr(5, 2), 16)} 
                  min="0" 
                  max="255"
                  onChange={(e) => {
                    const b = Math.min(255, Math.max(0, parseInt(e.target.value) || 0)).toString(16).padStart(2, '0');
                    const newColor = `#${currentColor.substr(1, 4)}${b}`;
                    handleColorChange({ target: { value: newColor } });
                  }}
                />
              </div>
            </div>
            
            <div className="color-modal-footer">
              <button 
                type="button"
                className="color-modal-btn cancel" 
                onClick={closeModal}
              >
                Cancelar
              </button>
              <button 
                type="button"
                className="color-modal-btn apply" 
                onClick={closeModal}
                style={{ backgroundColor: currentColor, color: getContrastColor(currentColor) }}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };
  
  return (
    <>
      <div className="color-preview-container">
        <button
          type="button"
          className="color-preview-button"
          onClick={openModal}
          style={{ backgroundColor: currentColor }}
          aria-label="Abrir seletor de cores"
        >
          <span className="color-value" style={{ color: getContrastColor(currentColor) }}>
            {currentColor}
          </span>
        </button>
      </div>
      
      {renderModal()}
    </>
  );
};

export default ColorPicker;
